"use client";

import { useEffect, useRef } from "react";
import type {
  ChatClientDataPart,
  ChatClientPlugin,
  ChatClientRequestBody,
} from "@/lib/ai/chat-client-plugin";
import type { ModelId } from "@/lib/ai/models";

/**
 * AI SDK `useChat` の stream 更新をまとめる wait。
 *
 * @remarks
 * streaming text は `Streamdown` を通るため、chunk ごとの即時反映だと
 * Markdown parse と subtree diff が過密になりやすい。公式 cookbook 寄りに
 * 約 50ms へ寄せ、入力や scroll を阻害しにくい頻度へ丸める。
 */
export const CHAT_STREAM_MESSAGE_THROTTLE_MS = 50;

type BuildChatClientRequestBodyArgs = {
  workspaceId: string;
  model: ModelId;
  chatId?: string | null;
  lang?: string;
  currentUrl?: string;
  plugins: ChatClientPlugin[];
};

/**
 * chat transport へ渡す request body を組み立てる。
 *
 * @remarks
 * thread 側は workspace/model/currentUrl などの共通入力だけを渡し、
 * 機能固有フィールドは plugin の `extendRequestBody` へ委譲する。
 */
export function buildChatClientRequestBody({
  workspaceId,
  model,
  chatId,
  lang,
  currentUrl,
  plugins,
}: BuildChatClientRequestBodyArgs): ChatClientRequestBody {
  const body: ChatClientRequestBody = {
    workspaceId,
    model,
  };

  if (chatId) {
    body.chatId = chatId;
  }
  if (lang) {
    body.lang = lang;
  }
  if (currentUrl) {
    body.currentUrl = currentUrl;
  }

  plugins.forEach((plugin) => {
    plugin.extendRequestBody?.(body);
  });

  return body;
}

/**
 * plugin が data part を消費したかを判定する。
 *
 * `true` を返した plugin がある場合、その data part は plugin 専用として扱う。
 */
export function handleChatClientPluginDataPart(
  plugins: ChatClientPlugin[],
  part: ChatClientDataPart,
): boolean {
  for (const plugin of plugins) {
    if (plugin.handleDataPart?.(part) === true) {
      return true;
    }
  }

  return false;
}

/**
 * plugin 側ポリシーにより chat エラー表示を抑止すべきかを判定する。
 */
export function shouldSuppressChatClientError(
  plugins: ChatClientPlugin[],
  error: Error,
): boolean {
  return plugins.some((plugin) => plugin.shouldSuppressError?.(error) === true);
}

/**
 * 会話スコープ切り替え時だけ plugin へリセット通知する。
 *
 * @remarks
 * draft -> persisted の昇格は同一会話として扱いたいため、scope ID が変わった時だけ
 * 通知する。
 */
export function useChatClientPluginThreadScopeChange(
  threadScopeId: string | null | undefined,
  plugins: ChatClientPlugin[],
) {
  const previousThreadScopeRef = useRef<string | null>(null);

  useEffect(() => {
    const nextScopeId = threadScopeId ?? null;
    if (previousThreadScopeRef.current === nextScopeId) {
      return;
    }

    previousThreadScopeRef.current = nextScopeId;
    plugins.forEach((plugin) => {
      plugin.onThreadScopeChange?.();
    });
  }, [threadScopeId, plugins]);
}
